import { Injectable } from '@angular/core';
import { applyTransaction } from '@datorama/akita';
import { ErrorHandlerService } from '@y42/error-handling';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import { combineLatest, first, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  AclOrgSpaceSettings,
  AclPermission,
  AclPermissionDetail,
  AclPermissionsRecord,
  AclResource,
  AclResourceType,
  AclRole,
  AclRoleAssignment,
  AclSelectRolesContext,
  AclStrategy,
  AclSubject,
} from '../models';
import { AclQuery } from '../state';
import { AclStore } from '../state/acl.store';
import { AclHttpService } from './acl-http.service';

type NullablePermissionsRecord = Record<AclPermission, AclPermissionDetail | null>;

@Injectable({ providedIn: 'root' })
export class AclService {
  constructor(
    private readonly aclHttpService: AclHttpService,
    private readonly aclStore: AclStore,
    private readonly aclQuery: AclQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private readonly rolesWithoutInheritedReadAccess = [AclRole.Member, AclRole.Guest];

  getSpaceSettings({ spaceId }: { spaceId: string }): Observable<AclOrgSpaceSettings> {
    return this.aclHttpService.getSpaceSettings({ spaceId });
  }

  setSpaceSettings({ spaceId, settings }: { spaceId: string; settings: AclOrgSpaceSettings }): Observable<boolean> {
    return this.aclHttpService.setSpaceSettings({ spaceId, settings });
  }

  checkPermission({
    resourceType,
    resourceId,
    permission,
    spaceId,
    strategy,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    permission: AclPermission;
    spaceId?: string;
    strategy?: AclStrategy;
  }): Observable<AclPermissionDetail> {
    return this.checkPermissions({
      resourceType,
      resourceId,
      permissions: [permission],
      spaceId,
      strategy,
    }).pipe(map((permissionsMap) => permissionsMap[permission] || { allowed: false }));
  }

  checkPermissions({
    resourceType,
    resourceId,
    permissions,
    spaceId,
    strategy = AclStrategy.ApiFirst,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    permissions: AclPermission[];
    spaceId?: string;
    strategy?: AclStrategy;
  }): Observable<AclPermissionsRecord> {
    if (strategy === AclStrategy.StoreFirst && !this.aclQuery.arePermissionsEmpty({ resourceType, resourceId })) {
      return of(
        permissions.reduce((permissionsMap, permission) => {
          const permissionDetail = this.aclQuery.getPermission({ resourceType, resourceId, permission, spaceId });
          permissionsMap[permission] = {
            allowed: !!permissionDetail?.allowed,
            detail: permissionDetail?.detail,
          };
          return permissionsMap;
        }, {} as AclPermissionsRecord),
      );
    }

    const hasPermissionsMap = permissions.reduce((permissionsMap, permission) => {
      permissionsMap[permission] = this.aclQuery.getPermission({ resourceType, resourceId, permission });
      return permissionsMap;
    }, {} as NullablePermissionsRecord);

    const notFoundPermissions = Object.entries(hasPermissionsMap)
      .filter(([_permission, hasPermission]) => isNullish(hasPermission))
      .map(([permission]) => permission as AclPermission);

    const foundPermissionsMap = Object.entries(hasPermissionsMap)
      .filter(([_permission, hasPermission]) => isNotNullish(hasPermission))
      .reduce((permissionsMap, [permission, permissionDetail]) => {
        assertNonNullable(permissionDetail);
        permissionsMap[permission as AclPermission] = permissionDetail;
        return permissionsMap;
      }, {} as AclPermissionsRecord);

    let apiPermissions$: Observable<AclPermissionsRecord>;

    if (notFoundPermissions.length > 0) {
      apiPermissions$ = this.aclHttpService
        .checkPermissions({ resourceType, resourceId, permissions: notFoundPermissions, spaceId })
        .pipe(
          map((resourcePermissions) =>
            resourcePermissions.permissions.reduce((apiPermissionsMap, resourcePermission) => {
              apiPermissionsMap[resourcePermission.permission] = {
                allowed: resourcePermission.allowed,
                detail: resourcePermission.detail,
              };
              return apiPermissionsMap;
            }, {} as AclPermissionsRecord),
          ),
          tap((apiPermissionsMap) => {
            this.aclStore.updatePermissions({
              resourceType,
              resourceId,
              permissionsMap: apiPermissionsMap,
              spaceId,
            });
          }),
        );
    } else {
      apiPermissions$ = of({} as AclPermissionsRecord);
    }

    return apiPermissions$.pipe(
      map((apiPermissions) => ({
        ...apiPermissions,
        ...foundPermissionsMap,
      })),
    );
  }

  loadRoles(): Observable<void> {
    return forkJoin([this.aclHttpService.getRoles(), this.aclHttpService.getRoleTooltips()]).pipe(
      tap(([roles, roleTooltips]) => {
        this.aclStore.setRoles(roles);
        this.aclStore.setRoleTooltips(roleTooltips);
      }),
      map(() => undefined),
    );
  }

  getResourceIdsByPermission({
    resourceType,
    permission,
    strategy = AclStrategy.ApiFirst,
  }: {
    resourceType: AclResourceType;
    permission: AclPermission;
    strategy?: AclStrategy;
  }): Observable<string[]> {
    const check$ =
      strategy === AclStrategy.ApiFirst
        ? this.enumerateResources({
            resourceType,
            permissions: [permission],
          })
        : of([]);
    return check$.pipe(map(() => this.aclQuery.getResourceIdsByPermission({ resourceType, permission })));
  }

  enumerateResources({
    resourceType,
    permissions,
    subject,
    spaceId,
    storeResources,
    skipStoreRoleAssignments,
  }: {
    resourceType: AclResourceType;
    permissions: AclPermission[];
    subject?: AclSubject;
    spaceId?: string;
    storeResources?: boolean;
    skipStoreRoleAssignments?: boolean;
  }): Observable<AclResource[]> {
    return this.aclHttpService
      .enumerateResources({
        resourceType,
        permissions,
        subject,
        spaceId,
      })
      .pipe(
        tap((resources) => {
          applyTransaction(() => {
            if (storeResources) {
              this.aclStore.setResources({
                resourceType,
                resources,
              });
            }

            // The permissions are not affected by the subject
            this.aclStore.addPermissions({
              resourceType,
              resources,
            });

            if (subject && !skipStoreRoleAssignments) {
              // Create and store the role assignments
              for (const resource of resources) {
                const roleAssignment = this.getRoleAssignmentFromResource(resource, subject);
                // When subject is explicitly provided in the http request, the resource role is
                // always filled
                if (roleAssignment) {
                  this.aclStore.updateRoleAssignments({
                    resourceType,
                    resourceId: resource.resource_id,
                    roleAssignments: [roleAssignment],
                  });
                }
              }
            }
          });
        }),
      );
  }

  getRoleAssignmentFromResource(resource: AclResource, subject: AclSubject): AclRoleAssignment | null {
    if (!resource.role) {
      return null;
    }

    return {
      subject,
      role: resource.role,
      direct_role: resource.direct_role,
    };
  }

  getRoleAssignments({
    resourceType,
    resourceId,
    spaceId,
    subject,
    strategy = AclStrategy.StoreFirst,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    subject?: AclSubject;
    strategy?: AclStrategy;
  }): Observable<AclRoleAssignment[]> {
    if (strategy === AclStrategy.ApiFirst) {
      return this.fetchRoleAssignments({
        resourceType,
        resourceId,
        spaceId,
        subject,
      });
    }

    return this.aclQuery
      .selectRoleAssignments({
        resourceId,
        resourceType,
      })
      .pipe(
        first(),
        map((roleAssignments) =>
          subject
            ? roleAssignments.filter(
                (assignment) =>
                  assignment.subject.subject_type === subject?.subject_type &&
                  assignment.subject.subject_id === subject?.subject_id,
              )
            : roleAssignments,
        ),
        switchMap((roleAsssignments) =>
          roleAsssignments.length > 0
            ? of(roleAsssignments)
            : this.fetchRoleAssignments({
                resourceType,
                resourceId,
                spaceId,
                subject,
              }),
        ),
      );
  }

  private fetchRoleAssignments({
    resourceType,
    resourceId,
    spaceId,
    subject,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    subject?: AclSubject;
  }): Observable<AclRoleAssignment[]> {
    return this.aclHttpService
      .getRoleAssignments({
        resourceType,
        resourceId,
        spaceId,
        subject,
      })
      .pipe(
        tap((roleAssignments) =>
          this.aclStore.updateRoleAssignments({
            resourceType,
            resourceId,
            roleAssignments,
          }),
        ),
      );
  }

  setRole({
    resourceType,
    resourceId,
    spaceId,
    subject,
    role,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    subject: AclSubject;
    role: AclRole;
  }): Observable<boolean> {
    return this.aclHttpService.setRole({ resourceType, resourceId, spaceId, subject, role }).pipe(
      switchMap(() =>
        this.getRoleAssignments({
          resourceType,
          resourceId,
          spaceId,
          strategy: AclStrategy.ApiFirst,
        }),
      ),
      map(() => true),
    );
  }

  revokeRole({
    resourceType,
    resourceId,
    spaceId,
    subject,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    subject: AclSubject;
  }): Observable<boolean> {
    return this.aclHttpService.revokeRole({ resourceType, resourceId, spaceId, subject }).pipe(
      switchMap(() =>
        this.getRoleAssignments({
          resourceType,
          resourceId,
          spaceId,
          strategy: AclStrategy.ApiFirst,
        }),
      ),
      map(() => true),
    );
  }

  convertRoleToGrantPermission(role: AclRole): AclPermission {
    return `grant_${role}` as AclPermission;
  }

  convertGrantPermissionToRole(permission: AclPermission): AclRole {
    return (permission as string).split('_').slice(1).join('_') as AclRole;
  }

  filterRolesBasedOnGrantedPermissions({
    roles,
    resourceType,
    resourceId,
    spaceId,
    strategy,
  }: {
    roles: AclRole[];
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    strategy?: AclStrategy;
  }): Observable<AclRole[]> {
    return this.checkPermissions({
      permissions: roles.map((role) => this.convertRoleToGrantPermission(role)),
      resourceType,
      resourceId,
      spaceId,
      strategy,
    }).pipe(
      map(
        (permissionsMap) =>
          Object.entries(permissionsMap)
            .map(([permission, hasPermission]) =>
              hasPermission.allowed ? this.convertGrantPermissionToRole(permission as AclPermission) : null,
            )
            .filter(Boolean) as AclRole[],
      ),
    );
  }

  isReadAccessInheritedWithRole(role: AclRole): boolean {
    return !this.rolesWithoutInheritedReadAccess.includes(role);
  }

  resetAllRoleAssignments({ resourceTypes }: { resourceTypes: AclResourceType[] }): void {
    applyTransaction(() => {
      for (const resourceType of resourceTypes) {
        this.aclStore.resetAllRoleAssignments({
          resourceType,
        });
      }
    });
  }

  resetAllPermissions({ resourceTypes }: { resourceTypes?: AclResourceType[] } = {}): void {
    if (!resourceTypes) {
      this.aclStore.resetAllPermissions();
      return;
    }

    applyTransaction(() => {
      for (const resourceType of resourceTypes) {
        this.aclStore.resetAllPermissions({
          resourceType,
        });
      }
    });
  }

  getRolesContextForRoleSelect({
    resourceId,
    resourceType,
    spaceId,
    subject,
    strategy,
  }: {
    resourceId: string;
    resourceType: AclResourceType;
    spaceId?: string;
    subject?: AclSubject;
    strategy?: AclStrategy;
  }): Observable<AclSelectRolesContext> {
    const roles = this.aclQuery.getRoles({ resourceType });

    const roleAssignment$ = this.aclQuery
      .selectRoleAssignments({
        resourceType,
        resourceId,
      })
      .pipe(
        first(),
        map((assignments) => {
          const assignment = assignments.find(
            (item) =>
              item.subject.subject_id === subject?.subject_id && item.subject.subject_type === subject.subject_type,
          );

          return {
            directRole: assignment?.direct_role,
            inheritedRole: assignment?.role,
          };
        }),
      );

    const grantedRoles$ = this.filterRolesBasedOnGrantedPermissions({
      roles,
      resourceId,
      resourceType,
      spaceId,
      strategy,
    }).pipe(
      map((filteredRoles) =>
        filteredRoles.map((role) => ({
          role,
          tooltip: this.aclQuery.getRoleTooltip({ role, resourceType }),
        })),
      ),
    );

    return combineLatest([roleAssignment$, grantedRoles$]).pipe(
      map(([{ directRole, inheritedRole }, grantedRoles]) => ({
        allRoles: roles,
        grantedRoles,
        directRole,
        inheritedRole,
        spaceId,
        resourceType,
      })),
    );
  }
}
